import { Box, CircularProgress, Divider, Grid, type GridProps } from "@mui/material";
import { type ReactElement, useCallback, useState } from "react";
import { HasPollsOrSurveys } from "../../../pages/moments/history/components/has-polls-or-surveys-cell";
import { useFlags } from "../../../server/optimizely";
import type { UUID } from "../../../utils/uuid";
import { useMomentClonable } from "../../moment/component/copy/utils";
import type { UseMomentProperties } from "../types";
import { CopyAction } from "./actions/copy";
import { EditAction } from "./actions/edit";
import { PublishToggleAction } from "./actions/publish-toggle";
import { ViewAction } from "./actions/view";
import { CardArchiveButton } from "./card-archive-button";

interface Props extends GridProps {
  moment: Pick<
    UseMomentProperties,
    | "id"
    | "title"
    | "status"
    | "valid_on_save"
    | "immediate"
    | "channel"
    | "only_additional_recipients"
    | "target_manager"
    | "target_connection_type"
    | "target_channel"
    | "notify_channel"
    | "use_lexical_editor"
    | "program"
    | "follow_up"
  >;
  small?: boolean;
  switchable?: boolean;
  reload?: () => void;
  surveyTitles?: string[];
  poll_ids?: UUID[];
}

export const MomentCardActions = ({
  moment,
  small,
  switchable = true,
  reload,
  surveyTitles,
  poll_ids,
  ...props
}: Props): ReactElement => {
  const { clonable, loading } = useMomentClonable(moment);
  const {
    flags: { "cha-4026-preview-before-publish": previewBeforePublish },
  } = useFlags("cha-4026-preview-before-publish");

  const { id, title, status } = moment;
  const [active, setActive] = useState(status === "active");

  const isArchived = status === "archived";
  const publishable = switchable && allowPublishToggle(moment, active);

  const handleReload = useCallback(
    (newActive: boolean) => {
      setActive(newActive);
      reload?.();
    },
    [reload],
  );

  if (loading) return <CircularProgress />;

  return (
    <Box display="flex" alignItems="center" {...props}>
      <HasPollsOrSurveys pollCount={poll_ids?.length || 0} surveyTitles={surveyTitles || []} />
      <Grid container justifyContent="flex-end" alignItems="center" flexGrow={1}>
        <Box>
          {id && (
            <CardArchiveButton
              id={id}
              isDeleted={isArchived}
              isArchived={isArchived}
              isActive={active}
              unsavedChanges={false}
              status={status}
              onClicked={reload}
            />
          )}
          {!active && (status === "draft" || status === "paused") ? (
            <EditAction id={id} title={title} follow_up_id={moment.follow_up?.id} />
          ) : (
            <ViewAction id={id} title={title} follow_up_id={moment.follow_up?.id} />
          )}
          {clonable && id && (
            <CopyAction
              id={id}
              title={title}
              clonable={clonable}
              disabled={
                !moment.program
                  ? { reason: "This Communication must have a Program selected before copying" }
                  : undefined
              }
            />
          )}
          {small && publishable && !previewBeforePublish && (
            <PublishToggleAction id={id} status={status} reload={handleReload} />
          )}
        </Box>
        {!small && publishable && !previewBeforePublish && (
          <>
            <Divider orientation="vertical" flexItem={true} sx={{ ml: 0.5 }} />
            {<PublishToggleAction id={id} status={status} reload={handleReload} />}
          </>
        )}
      </Grid>
    </Box>
  );
};

export function allowPublishToggle(
  moment: {
    id: UUID | null;
    status: "draft" | "paused" | "finished" | "active" | "archived" | null;
    valid_on_save: boolean | null;
    immediate: boolean | null;
  },
  active: boolean,
): boolean {
  const { status, valid_on_save, immediate } = moment;

  if (immediate && active) return false;
  if (status === "archived" || status === "finished") return false;

  return valid_on_save ?? false;
}
